import DeliveryBoardNavigationIcon from 'components/layout/assets/delivery-board-navigation-icon';
import { NavigationFeature } from 'components/layout/types/navigation-feature.enum';
import { UserFeatures } from 'types/user-features.enum';

import { AccountFeatures } from 'contexts/account/types/account-features.type';
import {
  Activity,
  BarChart2,
  FileChartColumnIncreasing,
  ChartLine,
  Clipboard,
  Compass,
  Cylinder,
  Droplet,
  FilePlus,
  FileText,
  Hammer,
  Home,
  Layers,
  LogOut,
  LucideIcon,
  Settings,
  Shrimp,
  Sliders,
  Target,
  Wrench,
  Zap,
} from 'lucide-react';
import { MarketId } from 'types/market-id.enum';
import { UserType } from 'types/shared/user-type.enum';
import { isProductionEnvironment, playgroundIds } from 'utils/environment';

export interface NavigationGroup {
  name: string;
  items: NavigationItem[];
}

interface NavigationItemBase {
  title: string;
  icon?: LucideIcon;
  customIcon?: any;
  bluViuFeature?: NavigationFeature;
  useCustomerName?: boolean;
}

export type NavigationItem = NavigationItemBase &
  (
    | {
        url: string;
        onClick?: never;
      }
    | {
        url?: never;
        onClick: () => void;
      }
  );

const userSettingsNavigation = (onLogout: () => void): NavigationGroup => ({
  name: 'User Settings',
  items: [
    {
      title: 'sidebar.settings',
      url: '/settings',
      icon: Settings,
      bluViuFeature: NavigationFeature.Overview,
      useCustomerName: true,
    },
    {
      title: 'sidebar.logout',
      icon: LogOut,
      onClick: onLogout,
      bluViuFeature: NavigationFeature.Overview,
    },
  ],
});

const testFeatures = (currentUserId?: string): NavigationItem[] =>
  playgroundIds.includes(currentUserId as string) || !isProductionEnvironment
    ? [
        {
          title: 'sidebar.playground',
          url: '/playground',
          customIcon: Hammer,
          bluViuFeature: NavigationFeature.Playground,
        },
      ]
    : [];

export const salmonNavigation = (currentUserId: string | undefined, onLogout: () => void): NavigationGroup[] => {
  return [
    {
      name: 'CustomerAccess',
      items: [
        {
          title: 'sidebar.overview',
          url: '/overview',
          icon: Home,
          bluViuFeature: NavigationFeature.Overview,
        },
        {
          title: 'sidebar.deliveryBoard',
          url: '/delivery-board',
          customIcon: DeliveryBoardNavigationIcon,
          bluViuFeature: NavigationFeature.DeliveryBoard,
        },
        {
          title: 'sidebar.orderFeed',
          url: '/order-feed',
          icon: Layers,
          bluViuFeature: NavigationFeature.OrderFeed,
        },
        // FieldRegistration = FishCheck

        {
          title: 'sidebar.fishCheck',
          url: '/fish-check',
          icon: Clipboard,
          bluViuFeature: NavigationFeature.FieldRegistration,
        },
        {
          title: 'sidebar.planningPrognosis',
          url: '/planning-prognosis',
          icon: Activity,
          bluViuFeature: NavigationFeature.PlanAndPrognosis,
        },
        {
          title: 'sidebar.reports',
          url: '/powerbi/reports',
          icon: FileChartColumnIncreasing,
          bluViuFeature: NavigationFeature.PowerBi,
        },
        {
          title: 'sidebar.seaCloudFileUpload',
          url: '/file-upload',
          icon: FilePlus,
          bluViuFeature: NavigationFeature.SeaCloudFileUpload,
        },
        {
          title: 'sidebar.fileSharing',
          url: '/file-sharing',
          icon: FileText,
          bluViuFeature: NavigationFeature.FileSharing,
        },
        {
          title: 'sidebar.bluViuNutrition',
          url: '/bluviu-nutrition',
          icon: Compass,
          bluViuFeature: NavigationFeature.ScenarioBuilder,
        },
        {
          title: 'sidebar.feedManagement',
          url: '/feed-management',
          icon: Zap,
          bluViuFeature: NavigationFeature.FeedManagement,
        },
        {
          title: 'sidebar.siteSettings',
          url: '/site-settings',
          icon: Sliders,
          bluViuFeature: NavigationFeature.SiteSettings,
        },
        {
          title: 'sidebar.profilesStrategies',
          url: '/profiles-strategies',
          icon: Droplet,
          bluViuFeature: NavigationFeature.ProfilesStrategies,
        },
        ...testFeatures(currentUserId as string),
      ],
    },
    userSettingsNavigation(onLogout),
  ];
};

export const shrimpNavigation = (currentUserId: string | undefined, onLogout: () => void): NavigationGroup[] => [
  {
    name: 'Customer Access',
    items: [
      {
        title: 'sidebar.overview',
        url: '/overview',
        icon: Home,
        bluViuFeature: NavigationFeature.Overview,
      },
      {
        title: 'sidebar.productionSystems',
        url: '/production-systems',
        icon: Shrimp,
        bluViuFeature: NavigationFeature.ShrimpProductionSystem,
      },
      {
        title: 'sidebar.benchmarkAnalytics',
        url: '/benchmark-analytics',
        icon: BarChart2,
        bluViuFeature: NavigationFeature.ShrimpBenchmarkAnalytics,
      },
      {
        title: 'sidebar.selfService',
        url: '/self-service',
        icon: Wrench,
        bluViuFeature: NavigationFeature.ShrimpSelfService,
      },
      {
        title: 'sidebar.fileSharing',
        url: '/file-sharing',
        icon: FileText,
        bluViuFeature: NavigationFeature.FileSharing,
      },
      {
        title: 'sidebar.reports',
        url: '/powerbi/reports',
        icon: FileChartColumnIncreasing,
        bluViuFeature: NavigationFeature.PowerBi,
      },
      {
        title: 'sidebar.shrimpDataUpload',
        url: '/shrimp-upload',
        icon: FilePlus,
        bluViuFeature: NavigationFeature.ShrimpDataUpload,
      },
      ...testFeatures(currentUserId as string),
    ],
  },
  userSettingsNavigation(onLogout),
];

export const hasAccess = (
  item: NavigationItem,
  access: AccountFeatures,
  userCompanyFeatures: number[],
  userFeatures: number[],
  userType?: UserType,
  marketId?: MarketId,
  currentUserId?: string
) => {
  switch (item.bluViuFeature) {
    case NavigationFeature.Overview:
      return true;
    case NavigationFeature.DeliveryBoard:
      return access.hasOrderRead && marketId === MarketId.Norway;

    case NavigationFeature.SiteSettings:
      return access.hasSiteSettingsRead || access.hasSiteSettingsWrite;

    case NavigationFeature.FieldRegistration:
      return access.hasFieldRegistration;

    case NavigationFeature.OrderFeed:
      return access.hasOrderFeed || access.hasOrderRead;

    case NavigationFeature.ProfilesStrategies:
      return (
        access.hasProfilesStrategies ||
        access.hasPlanningPrognosisManageCompanyOrSiteProfiles ||
        access.hasPlanningPrognosisManageMarketProfiles
      );

    case NavigationFeature.PlanAndPrognosis:
      return access.hasPlanningPrognosis || access.hasPlanningPrognosisReadonly;

    case NavigationFeature.ScenarioBuilder:
      return access.hasScenarioBuilder;

    case NavigationFeature.PowerBi:
      return true;

    case NavigationFeature.FileSharing:
      return true;

    case NavigationFeature.SeaCloudFileUpload:
      return access.hasUploadFilesToSeaCloud;

    case NavigationFeature.FeedManagement:
      return (
        // The FeedManagement is available only to users that have the role UserFeatures.Nutritionist
        // This feature can be added to an Admin i.e. internal (Cargill) user via an admin role
        // Note : In Feed Management
        // The tabs [BestMix Feeds, Manual Feeds, Customers] should be available if the user has the mentioned feature (UserFeatures.Nutritionist)
        // The tabs [Plants, Nutrients] should only be available to users of type SuperUser.
        (userFeatures.includes(UserFeatures.Nutritionist) || userType == UserType.SuperUser)
      );
    case NavigationFeature.ShrimpProductionSystem:
      return access.hasShrimpsProductionSystem;
    case NavigationFeature.ShrimpBenchmarkAnalytics:
      return userCompanyFeatures.includes(UserFeatures.ShrimpBenchmarkAnalytics);
    case NavigationFeature.ShrimpSelfService:
      return userCompanyFeatures.includes(UserFeatures.SelfService)
    case NavigationFeature.ShrimpDataUpload:
      return (
        // The Shrimp Data Upload menu is available only to (internal) users that have the feature UserFeatures.ShrimpDataUpload
        (userFeatures.includes(UserFeatures.ShrimpDataUpload))
      );
    case NavigationFeature.Playground:
      return !isProductionEnvironment;
    default:
      return false;
  }
};
